import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { HtmlRenderer } from 'shared-components';
import {
  ActionWrapper,
  DCard,
  PrimaryButton,
  RedeemSteps,
  RedeemTitle,
} from '../style';
import { loginDisbursements } from '../services';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import { Container } from '../TransferOptions/styles';
import { Spinner } from '../InteracTransfer/styles';
import { IdentityIcon, LocalAtm } from '../../../components/Icons';

export default function RefundPage() {
  const navigate = useNavigate();
  const {
    setVerificationToken,
    setVerificationTokenExpiry,
    setError,
    settransferSuccessData,
    resetSession,
  } = useContext(DisbursementsContext);
  const [loading, setLoading] = useState(false);
  const intl = useIntl();
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    let isMounted = true;

    const validateTokenIfMounted = async tkn => {
      try {
        const res = await loginDisbursements(tkn);
        if (!isMounted) return;

        const data = res && res.data ? res.data : {};
        const { status, expires_at: expiresAt } = data;
        const redeemType = data.redeem_type;

        // notification flow
        if (status === 'notification_sent') {
          setVerificationToken(tkn);
          // Convert expires_at to milliseconds timestamp if provided
          setVerificationTokenExpiry(expiresAt ? new Date(expiresAt).getTime() : null);
          setError(null);
          return;
        }

        // completed / awaiting settlement flows
        const isSuccess =
          status === 'completed' || status === 'awaiting_settlement';
        const isPushType =
          redeemType === 'etransfer' || redeemType === 'visa_direct';

        if (status === 'completed' && redeemType === 'keep_card') {
          settransferSuccessData(data);
          navigate('/disbursements/account-created');
          return;
        }

        if (isSuccess && isPushType) {
          settransferSuccessData(data);
          navigate(
            `/disbursements/${
              redeemType === 'etransfer' ? 'interac' : 'transfer'
            }-success?exception=completed`
          );
          return;
        }

        // fallback
        setError('link_expired');
      } catch (err) {
        if (!isMounted) return;

        const code = err && err.error ? err.error.code : undefined;
        if (code === 'account_locked') {
          navigate('/disbursements/verification-failed');
        } else {
          setError('link_expired');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (token) {
      resetSession();
      setLoading(true);
      validateTokenIfMounted(token);
    } else {
      setError('link_expired');
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-white">
      {loading ? (
        <Container
          style={{
            paddingTop: '2rem',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner style={{ width: '2rem', height: '2rem' }} />
        </Container>
      ) : (
        <div className="max-w-4xl mx-auto p-4 sm:p-6">
          {/* Main Heading */}
          <RedeemTitle>{intl.messages['refund.title']}</RedeemTitle>
          {/* Combined Steps Section */}
          <DCard>
            <RedeemSteps className="space-y-8">
              {/* Step 1 */}
              <div className="redeem-step">
                <div className="step-info">
                  <h3>
                    <span>1. </span>
                    {intl.messages['refund.step1.title']}
                  </h3>
                  <div className="step-description">
                    <p>{intl.messages['refund.step1.description']}</p>
                    <IdentityIcon className="step-icon" />
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="redeem-step">
                <div className="step-info">
                  <h3>
                    <span>2. </span>
                    {intl.messages['refund.step2.title']}
                  </h3>
                  <div className="step-description">
                    <p><HtmlRenderer html={intl.messages['refund.step2.description']} /></p>
                    <LocalAtm className="step-icon" />
                  </div>
                </div>
              </div>
            </RedeemSteps>
          </DCard>

          {/* Action Button */}
          <ActionWrapper>
            <PrimaryButton
              disabled={loading}
              onClick={() => navigate('/disbursements/verification')}
            >
              {intl.messages['refund.button']}
            </PrimaryButton>
          </ActionWrapper>
        </div>
      )}
    </div>
  );
}

